import { createContext, useContext, useEffect } from "react";
import useInitTencentMessages from "../hooks/useTencentMessages";
import { AppContext } from "./AppContext";
import TencentCloudChat, { ChatSDK } from "@tencentcloud/chat";
import useTencentNotifications from "../hooks/useTencentNotifications";
import useTencentChat from "../hooks/useTencentChat";
import { MessageType } from "../commons/constants";

interface AppChat {
  userSig?: string;
  userID?: string;
  userSigExpireTime?: number;
  chat?: ChatSDK;
  handleListenMessageFromTencent: () => void;
  selectedFromListConversation: TridentFoodMessage[];
  setSelectedFromListConversation: (message: TridentFoodMessage[]) => void;
  notifications: TridentFoodNotifications[];
  onChangeTableNotifications: (page: number, pageSize: number) => void;
  metadataNotifications: PaginationMetadata;
  loadingNotifications: boolean;
  handleReadNotification: (item: any) => void;
  unreadNotifications: number;
  handleReadAllNotifications: () => void;
}

export const AppTencentContext = createContext<AppChat>({
  userSig: "",
  userID: "",
  userSigExpireTime: 0,
  chat: undefined,
  handleListenMessageFromTencent: () => {},
  selectedFromListConversation: [],
  setSelectedFromListConversation: () => {},
  notifications: [],
  onChangeTableNotifications: () => {},
  metadataNotifications: { "x-next-page": 0, "x-page": 0, "x-pages-count": 0, "x-per-page": 0, "x-total-count": 0 },
  loadingNotifications: false,
  handleReadNotification: () => {},
  unreadNotifications: 0,
  handleReadAllNotifications: () => {},
});

export const AppTencentProvider: React.FC<{ children: React.ReactNode }> = props => {
  const { user } = useContext(AppContext);
  const { userID, userSig, chat } = useInitTencentMessages(user?.id || "");
  const {
    notifications,
    handlePushNotificationsRecieved,
    onChangeTableNotifications,
    metadataNotifications,
    loadingNotifications,
    handleReadNotification,
    unreadNotifications,
    handleReadAllNotifications,
  } = useTencentNotifications(user);
  const { handlePushMessagesRecieved, selectedFromListConversation, setSelectedFromListConversation } =
    useTencentChat();

  function handleListenMessageFromTencent() {
    chat?.on(TencentCloudChat.EVENT.MESSAGE_RECEIVED, (message: { data: TencentMessage[] }) => {
      if (message.data[0].conversationType === MessageType.Notifications) {
        handlePushNotificationsRecieved(message.data[0]);
      } else {
        handlePushMessagesRecieved(message.data[0]);
      }
    });
  }

  useEffect(() => {
    if (chat) handleListenMessageFromTencent();
  }, [chat]);

  const tencentState = {
    userSig,
    userID,
    userSigExpireTime: 0,
    chat,
    handleListenMessageFromTencent,
    selectedFromListConversation,
    setSelectedFromListConversation,
    notifications,
    onChangeTableNotifications,
    metadataNotifications,
    loadingNotifications,
    handleReadNotification,
    unreadNotifications,
    handleReadAllNotifications,
  };

  return <AppTencentContext.Provider value={tencentState}>{props.children}</AppTencentContext.Provider>;
};
